import type { ApexOptions } from 'apexcharts'
import {
  DEFAULT_TIMEFRAME,
  resolveDisplay,
  type ChartCard,
  type Timeframe,
} from '@/features/dashboard/api/schemas'
import { axisMin, niceAxis, tickLabel } from '@/features/dashboard/format/axis'
import { previousPeriodLabel } from '@/features/dashboard/format/figure'
import {
  findSeries,
  latestDataIndex,
  metricSeries,
  OUTLOOK_SERIES,
  referenceColorFor,
  referenceSeries,
  seriesLabel,
} from './series'
import {
  chartTotals,
  formatMonthLong,
  formatMonthShort,
} from '@/features/dashboard/format/format'
import {
  emphasiseLabels,
  clickedIndexOf,
  dataPointIndexOf,
  drawGhosts,
  hideBarRing,
  liftBarsOffAxis,
  markBars,
  shapeBars,
  showBarRing,
  showMarkers,
  TOP_RADIUS_PX,
} from './apexDom'
import { AXIS_FONT_SIZE, baseOptions, type ChartPalette } from './chartTheme'
import { tooltipHtml } from './tooltipHtml'

const MAX_BAR_PX = 120
const BAR_INSET_PX = 8
/** The plot's padding either side, and the y-axis labels' room (about 52px). */
const PLOT_PADDING_PX = 16
const PLOT_RESERVE_PX = 80
/** A benchmark's line, and the ring of the dot marking each month. */
const LINE_PX = 4

/** Series a timeframe leaves out altogether. */
const IGNORED_SERIES: Record<Timeframe, string[]> = {
  Monthly: [OUTLOOK_SERIES, 'metric_monthly_forecast'],
  Quarterly: [OUTLOOK_SERIES, 'metric_monthly_forecast'],
  // The year is measured by the forecasts alone, not the original budgets.
  Annual: [
    'metric_monthly_forecast',
    'metric_budget_base',
    'metric_budget_upside',
  ],
  YTD: ['metric_monthly_forecast'],
}

/** What a year's reported value is called beside its outlook. */
const YEAR_TO_DATE_LABEL = 'YTD monthly'

/** The width one x label needs; closer bars show every nth label. */
const X_LABEL_MIN_PX = 64

/** What the expanded chart plots, derived from its card alone. */
export const expandedBarModel = (
  card: ChartCard,
  timeframe?: Timeframe,
  /** The bar the panel speaks of; the latest reported one when unset. */
  selectedIndex?: number
) => {
  // Multiple series are summed into one bar per category; benchmarks never are.
  const totals = chartTotals(card)
  const metrics = metricSeries(card.series)
  const reported = card.categories.map((_, i) =>
    metrics.length > 0
      ? metrics.some(series => typeof series.data[i] === 'number')
      : typeof totals[i] === 'number'
  )
  const values = card.categories.map(
    (_, i) =>
      totals[i] ??
      metrics.reduce((sum, series) => sum + (series.data[i] ?? 0), 0)
  )
  const allReferences = referenceSeries(card.series)
  // The outlook is not a line: in the YTD view it fills the months still to
  // come with pale bars, up to the year's end; in the Annual view it extends
  // the year's bar to its projection, as a pale ghost behind it.
  const outlook =
    timeframe === 'YTD' || timeframe === 'Annual'
      ? findSeries(allReferences, OUTLOOK_SERIES)
      : undefined
  const projected = card.categories.map(
    (_, i) =>
      timeframe === 'YTD' &&
      !reported[i] &&
      typeof outlook?.data[i] === 'number'
  )
  const ghosts = card.categories.map((_, i) => {
    const projection = outlook?.data[i]
    return timeframe === 'Annual' &&
      reported[i] &&
      typeof projection === 'number' &&
      projection > (values[i] ?? 0)
      ? projection
      : null
  })
  const barValues = values.map((value, i) =>
    projected[i] ? (outlook?.data[i] ?? value) : value
  )
  const ignored = (IGNORED_SERIES[timeframe ?? DEFAULT_TIMEFRAME] ?? []).map(
    name => name.toLowerCase()
  )
  // Benchmarks are lines next to the bars, on the same axis.
  const references = allReferences
    .filter(series => !ignored.includes(series.name.toLowerCase()))
    .filter(series => outlook === undefined || series !== outlook)
    .map(series => ({
      key: series.name,
      name: seriesLabel(series.name),
      data: card.categories.map((_, i) => series.data[i] ?? null),
    }))
  const referenceValues = references.flatMap(({ data }) =>
    data.filter((value): value is number => value !== null)
  )
  // Months with neither a value nor an outlook are empty, not zero.
  const drawn = barValues.filter((_, i) => reported[i] || projected[i])
  const ghostValues = ghosts.filter((value): value is number => value !== null)
  const lowest = Math.min(...drawn, ...referenceValues)
  // An all-positive chart doesn't start its axis at 0, which would flatten the
  // variation between bars (the design's '300M*'); the lowest label is starred.
  const lo = axisMin(lowest)
  const hi = Math.max(0, ...barValues, ...referenceValues, ...ghostValues)
  const axis = niceAxis(lo, hi)
  // The bar the panel speaks of: the green one.
  const selected = selectedIndex ?? latestDataIndex(card)
  return {
    values: barValues,
    projected,
    /** What a bar's change compares: the bar before it. */
    comparison: previousPeriodLabel(timeframe),
    /** Per bar, the outlook above it to draw as a ghost, if any. */
    ghosts,
    selected,
    /** What each bar is, for the CSS (see `markBars`). */
    kinds: barValues.map((_, i) =>
      projected[i] ? 'projected' : i === selected ? 'selected' : undefined
    ),
    references,
    labels: card.categories.map(formatMonthShort),
    axis,
    /** The axis starts above 0, so its lowest label is starred. */
    truncated: axis.min > 0,
    // Bars grow from 0, so only an all-positive chart lifts off the axis.
    allPositive: lo >= 0,
  }
}

export type ExpandedBarModel = ReturnType<typeof expandedBarModel>

/**
 * The bars, with any benchmarks as lines over them. Mixing the two needs each
 * series to name its type, which a plain bar chart leaves out.
 */
export const expandedBarSeries = (
  card: ChartCard,
  model: ExpandedBarModel
): { name: string; type?: string; data: (number | null)[] }[] =>
  model.references.length === 0
    ? [{ name: card.name, data: model.values }]
    : [
        { name: card.name, type: 'column', data: model.values },
        ...model.references.map(({ name, data }) => ({
          name,
          type: 'line',
          data,
        })),
      ]

/** Every nth x label is drawn, counting back from the latest. */
export const labelStride = (barPx: number) =>
  Math.max(1, Math.ceil(X_LABEL_MIN_PX / (barPx + BAR_INSET_PX)))

/** The width of a bar, given the chart's width and how many bars share it. */
export const barWidthPx = (width: number, bars: number) =>
  Math.max(
    1,
    Math.min(
      MAX_BAR_PX,
      Math.floor((width - PLOT_RESERVE_PX) / Math.max(bars, 1) - BAR_INSET_PX)
    )
  )

export const expandedBarOptions = (
  card: ChartCard,
  model: ExpandedBarModel,
  palette: ChartPalette,
  barPx: number,
  /** Called with the index of a reported bar that was clicked. */
  onSelect?: (index: number) => void
): ApexOptions => {
  const {
    values,
    references,
    labels,
    axis,
    allPositive,
    truncated,
    kinds,
    projected,
    ghosts,
    selected,
    comparison,
  } = model
  const base = baseOptions(palette)
  const selectedLabel = labels[selected] ?? null
  const stride = labelStride(barPx)

  // Lifts the bars off the axis, shapes them, marks the selected and projected
  // ones and emphasises the selected one's label.
  const decorate = (chart: unknown) => {
    markBars(chart, kinds)
    emphasiseLabels(chart, selectedLabel)
    showMarkers(chart)
    if (!allPositive) return
    liftBarsOffAxis(chart)
    shapeBars(chart, axis.min, values)
    drawGhosts(chart, axis.min, ghosts)
  }

  return {
    ...base,
    chart: {
      ...base.chart,
      type: references.length > 0 ? 'line' : 'bar',
      zoom: { enabled: false },
      selection: { enabled: false },
      // Apex redraws bars on every animation frame, undoing `shapeBars`.
      animations: { enabled: false },
      // `ExpandedBarChart` remounts the chart on a resize.
      redrawOnWindowResize: false,
      redrawOnParentResize: false,
      events: {
        mounted: decorate,
        updated: decorate,
        dataPointMouseEnter: (_event, chart, options) => {
          const index = dataPointIndexOf(options)
          if (index !== null) showBarRing(chart, index)
          emphasiseLabels(
            chart,
            selectedLabel,
            index === null ? null : (labels[index] ?? null)
          )
        },
        dataPointMouseLeave: (_event, chart) => {
          hideBarRing(chart)
          emphasiseLabels(chart, selectedLabel)
        },
        // A reported bar (or its benchmark marker) selects its month.
        click: (_event, _chart, options) => {
          const index = clickedIndexOf(options)
          if (index !== null && !projected[index]) onSelect?.(index)
        },
      },
    },
    colors: [
      palette.bar,
      ...references.map(({ key }, index) =>
        referenceColorFor(key, palette, index)
      ),
    ],
    ...(references.length > 0 && {
      // Bars have no outline; each benchmark is a line over them, a ringed dot
      // marking each month.
      stroke: {
        curve: 'straight',
        width: [0, ...references.map(() => LINE_PX)],
        lineCap: 'round',
        dashArray: 0,
      },
      markers: {
        size: [0, ...references.map(() => 4)],
        colors: [palette.card, ...references.map(() => palette.card)],
        strokeColors: [
          palette.bar,
          ...references.map(({ key }, index) =>
            referenceColorFor(key, palette, index)
          ),
        ],
        strokeWidth: LINE_PX,
        hover: { size: 6 },
      },
    }),
    // Solid sand: Apex's default bar fill is 85% opaque.
    fill: { opacity: 1 },
    plotOptions: {
      bar: {
        borderRadius: Math.min(TOP_RADIUS_PX, barPx / 2),
        borderRadiusApplication: 'end',
        columnWidth: `${barPx}px`,
      },
    },
    // The hover colour comes from the CSS, since Apex's darken filter all but
    // hides the sand bars in dark mode.
    states: {
      hover: { filter: { type: 'none' } },
      active: { filter: { type: 'none' } },
    },
    grid: {
      show: true,
      borderColor: palette.barGrid,
      strokeDashArray: 6,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      // Room for the target's label above the tallest bar.
      padding: {
        left: PLOT_PADDING_PX,
        right: PLOT_PADDING_PX,
        bottom: 0,
        top: 0,
      },
    },
    xaxis: {
      categories: labels,
      labels: {
        rotate: 0,
        // Thinned here (see labelStride): Apex's own check does so unevenly.
        hideOverlappingLabels: false,
        formatter: (value: string | number, _timestamp, opts) =>
          typeof opts?.i !== 'number' ||
          (labels.length - 1 - opts.i) % stride === 0
            ? String(value)
            : '',
        style: { colors: palette.textAlt, fontSize: AXIS_FONT_SIZE },
      },
      axisBorder: { show: true, color: palette.baseline },
      axisTicks: { show: false },
      tooltip: { enabled: false },
      crosshairs: { show: false },
    },
    yaxis: {
      min: axis.min,
      max: axis.max,
      tickAmount: axis.ticks,
      // The labels are at the chart's right edge, as in the design.
      opposite: true,
      labels: {
        style: { colors: palette.textAlt, fontSize: AXIS_FONT_SIZE },
        formatter: (value: number) =>
          `${tickLabel(value, card.format)}${truncated && value === axis.min ? '*' : ''}`,
      },
    },
    legend: { show: false },
    tooltip: {
      enabled: true,
      // Anywhere in a bar's column: also over its ghost and its benchmarks' lines.
      shared: true,
      intersect: false,
      followCursor: true,
      custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
        const display = resolveDisplay(card)
        const ghost = ghosts[dataPointIndex] ?? null
        const reported = values[dataPointIndex] ?? 0
        const lines = references
          .flatMap(({ key, name, data }, index) => {
            const value = data[dataPointIndex]
            return value === null || value === undefined
              ? []
              : [
                  {
                    label: name,
                    value,
                    color: referenceColorFor(key, palette, index),
                    upside: /upside/i.test(key),
                  },
                ]
          })
          // The upside is listed first, as in the side panel.
          .sort((a, b) => Number(b.upside) - Number(a.upside))
        return tooltipHtml({
          label: formatMonthLong(card.categories[dataPointIndex] ?? ''),
          // A year under way leads with its outlook; what is reported so far is
          // listed beneath it.
          value: ghost ?? reported,
          // A month still to come is forecast: it has no change to show.
          previous:
            dataPointIndex > 0 && !projected[dataPointIndex]
              ? (values[dataPointIndex - 1] ?? null)
              : null,
          changeLabel: comparison,
          unit: card.unit,
          palette,
          lowerIsBetter: display.lowerIsBetter,
          changeUnit: display.changeUnit,
          format: card.format,
          references:
            ghost === null
              ? lines
              : [
                  {
                    label: YEAR_TO_DATE_LABEL,
                    value: reported,
                    color: palette.bar,
                    square: true,
                  },
                  ...lines,
                ],
        })
      },
    },
  }
}
